import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Gavel, MapPin, IndianRupee, Calendar, Eye, FileText, Shield, CheckCircle2, TriangleAlert, Download, ExternalLink, Clock, User, Building2, ArrowRight } from 'lucide-react';
import './EAuction.css';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { scrollToSection } from '../../utils/helpers';
import AuctionEnquiryModal from './AuctionEnquiryModal';


const EAuction = () => {
  const backendBaseURL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001').replace('/api', '');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [bannerData, setBannerData] = useState(null);
  const [processData, setProcessData] = useState(null);
  const [listingsSettings, setListingsSettings] = useState(null);
  const [gettingStartedSettings, setGettingStartedSettings] = useState(null);
  const [steps, setSteps] = useState([]);
  const [secureBiddingSettings, setSecureBiddingSettings] = useState(null);
  const [legalSettings, setLegalSettings] = useState(null);
  const [importantPoints, setImportantPoints] = useState([]);
  const [loading, setLoading] = useState(true);


  // Enquiry Modal State
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState('');

  const handleEnquireClick = (propertyTitle) => {
    setSelectedProperty(propertyTitle);
    setIsEnquiryModalOpen(true);
  };

  const iconMap = {
    Eye: Eye,
    FileText: FileText,
    IndianRupee: IndianRupee,
    User: User,
    Gavel: Gavel,
    Shield: Shield
  };

  // Fetch E-Auction data (Banner + Process)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch Banner
        const bannerResponse = await axios.get('/api/admin/banners/page/e_auction');
        const banners = bannerResponse.data.data || [];
        if (banners.length > 0) {
          setBannerData(banners[0]);
        }

        // Fetch Process Section
        try {
          const processResponse = await axios.get('/api/admin/loan-overview/e_auction_process');
          if (processResponse.data.data && processResponse.data.data.is_active) {
            setProcessData(processResponse.data.data);
          }
        } catch (err) {
          console.log("Process section data not found or error fetching", err);
        }

        // Fetch Listings Settings
        try {
          const settingsResponse = await axios.get('/api/admin/e-auction-properties/settings');
          if (settingsResponse.data.success) {
            setListingsSettings(settingsResponse.data.data);
          }
        } catch (err) {
          console.log("Listings settings fetching error", err);
        }

        // Fetch Getting Started Settings
        try {
          const gettingStartedResponse = await axios.get('/api/admin/e-auction-properties/settings/getting-started');
          if (gettingStartedResponse.data.success) {
            setGettingStartedSettings(gettingStartedResponse.data.data);
          }
        } catch (err) {
          console.log("Getting started settings fetching error", err);
        }

        // Fetch Steps
        try {
          const stepsResponse = await axios.get('/api/admin/e-auction-properties/steps');
          if (stepsResponse.data.success) {
            setSteps(stepsResponse.data.data || []);
          }
        } catch (err) {
          console.log("Steps fetching error", err);
        }

        // Fetch Secure Bidding Settings
        try {
          const secureBiddingResponse = await axios.get('/api/admin/e-auction-properties/settings/secure-bidding');
          if (secureBiddingResponse.data.success && secureBiddingResponse.data.data) {
            setSecureBiddingSettings(secureBiddingResponse.data.data);
          }
        } catch (err) {
          console.log("Secure bidding settings fetching error", err);
        }

        // Fetch Legal Information Settings
        try {
          const legalResponse = await axios.get('/api/admin/e-auction-properties/settings/legal');
          if (legalResponse.data.success && legalResponse.data.data) {
            setLegalSettings(legalResponse.data.data);
          }
        } catch (err) {
          console.log("Legal settings fetching error", err);
        }

        // Fetch Important Points
        try {
          const pointsResponse = await axios.get('/api/admin/e-auction-properties/important-points');
          if (pointsResponse.data.success) {
            setImportantPoints(pointsResponse.data.data || []);
          }
        } catch (err) {
          console.log("Important points fetching error", err);
        }
      } catch (error) {
        console.error('Error fetching E-Auction data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const principles = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Open and transparent bidding',
      color: 'blue'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Equal opportunity for all participants',
      color: 'green'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Compliance with NHB / RBI and statutory guidelines',
      color: 'gold'
    }
  ];

  // Fetch Properties
  const [auctionProperties, setAuctionProperties] = useState([]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get('/api/admin/e-auction-properties/active');
        if (response.data.success) {
           const allProps = response.data.data || [];
          // Ensure only properties with is_active are shown on the frontend
          const activeProps = allProps.filter(prop => prop.is_active === 1 || prop.is_active === true);
          setAuctionProperties(activeProps);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };
    fetchProperties();
  }, []);

  const participationSteps = [
    {
      number: '01',
      title: 'Review Property Details',
      description: 'Browse listings and confirm your interest',
      icon: <Eye className="w-5 h-5" />
    },
    {
      number: '02',
      title: 'Submit KYC Documents',
      description: 'Complete verification process',
      icon: <FileText className="w-5 h-5" />
    },
    {
      number: '03',
      title: 'Pay EMD & Register Deposit',
      description: 'Deposit earnest money within timeline',
      icon: <IndianRupee className="w-5 h-5" />
    },
    {
      number: '04',
      title: 'Get Login Credentials',
      description: 'Access the e-auction portal',
      icon: <User className="w-5 h-5" />
    },
    {
      number: '05',
      title: 'Participate in Bidding',
      description: 'Place bids during auction window',
      icon: <Gavel className="w-5 h-5" />
    }
  ];

  const fallbackImportantPoints = [
    'Bidders are responsible for conducting their independent due diligence',
    'Final sale is subject to confirmation by the Company',
    'All payments must be made through permitted channels only'
  ];

  return (
    <div className="e-auction">

      <SEO
        title="Aham Housing Finance E-Auction | Bank Auction Properties"
        description="View Aham Housing Finance e-auction sale notices, reserve prices and bank auction properties. Bid online with a transparent, simple SARFAESI process."
        keywords="Aham Housing Finance e-auction, Aham e-auction, e-auction sale notice, property e-auction, secured asset auction, SARFAESI auction, Aham auction notice, online property auction, mortgaged property sale, bank auction property, housing finance auction, authorised officer auction notice, reserve price auction, EMD auction process, auctionbazaar property auction, distressed property auction, public e-auction notice, immovable property auction, secured creditor sale notice, Aham Housing Finance auction"
        canonical="https://ahamhfc.com/aham-housing-finance-e-auction-sale-notice"
        ogTitle="Aham Housing Finance E-Auction | Bank Auction Properties"
        ogDescription="View Aham Housing Finance e-auction sale notices, reserve prices and bank auction properties. Bid online with a transparent, simple SARFAESI process."
      />

      {/* Hero Banner */}
      {loading ? (
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-slate-200 animate-pulse">
          <div className="relative z-10 container mx-auto px-6 h-full flex items-center">
            <div className="max-w-3xl">
              <div className="h-8 bg-slate-300 rounded w-3/4 mb-4"></div>
              <div className="h-12 bg-slate-300 rounded w-full mb-4"></div>
              <div className="h-6 bg-slate-300 rounded w-5/6"></div>
            </div>
          </div>
        </section>
      ) : bannerData ? (
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('${bannerData.image_url || '/img/Banners/E-Auction.webp'}')`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
          </div>

          <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white mb-6 border border-white/20">
                <Gavel className="w-5 h-5" />
                <span className="font-medium">Legal & Compliant Process</span>
              </div>
              <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                {bannerData.title || 'E-Auction'}
              </h1>
              <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
                {bannerData.subtitle || 'Transparent, fair, and compliant asset disposal process. Browse auction listings, review property details, and participate securely through our e-auction platform.'}
              </p>

              {/* Breadcrumb Navigation */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="flex items-center space-x-2 mb-6 md:mb-8"
              >
                <Link
                  to="/"
                  className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                >
                  Home
                </Link>
                <ChevronRightIcon className="h-4 w-4 text-white/60" />
                <span className="text-yellow-300 text-sm font-semibold">Property for Sale</span>
              </motion.div>

              {/* CTA Buttons */}
              {bannerData.primary_button_text && (
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => scrollToSection('available-properties')}
                    className="inline-flex items-center gap-2 bg-white text-primary-900 px-6 py-3 rounded-xl font-semibold hover:bg-white/90 transition-all duration-200 shadow-lg"
                  >
                    {bannerData.primary_button_text}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  {bannerData.secondary_button_text && (
                    <a
                      href={bannerData.secondary_button_link || '#'}
                      className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all duration-200 border border-white/30"
                    >
                      {bannerData.secondary_button_text}
                    </a>
                  )}
                </div>
              )}

            </motion.div>
          </div>
        </section>
      ) : (
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('/img/Banners/E-Auction.webp')",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
          </div>

          <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white mb-6 border border-white/20">
                <Gavel className="w-5 h-5" />
                <span className="font-medium">Legal & Compliant Process</span>
              </div>
              <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                E-Auction
              </h1>
              <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
                Transparent, fair, and compliant asset disposal process. Browse auction listings, review property details, and participate securely through our e-auction platform.
              </p>

              {/* Breadcrumb Navigation */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="flex items-center space-x-2 mb-6"
              >
                <Link
                  to="/"
                  className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                >
                  Home
                </Link>
                <ChevronRightIcon className="h-4 w-4 text-white/60" />
                <span className="text-yellow-300 text-sm font-semibold">E-Auction</span>
              </motion.div>

            </motion.div>
          </div>
        </section>
      )}

      {/* Section 1: About E-Auction */}
      <section className="about-auction-section">
        <div className="section-container">
          <div className="about-auction-layout">
            <div className="about-auction-content text-center lg:text-left">
              <span className="section-tag mx-auto lg:mx-0">Understanding the Process</span>
              <h2 className="section-title mx-auto lg:mx-0">{processData?.title || 'What is an E-Auction?'}</h2>
              <div className="about-text">
                <p className="lead-paragraph whitespace-pre-wrap">
                  {processData?.description || `E-Auctions are conducted as part of the lawful recovery process under applicable regulations.
                  Properties offered for auction are those secured against loans where the borrower has defaulted despite repeated reminders and support options.
                  
                  The auction process is transparent, time-bound, and governed by regulatory guidelines, ensuring fairness to all participants.`}
                </p>
              </div>
            </div>

            <Card className="principles-card">
              <h3 className="principles-heading">Key Principles</h3>
              <div className="principles-list">
                {(() => {
                  let features = principles;
                  if (processData?.features) {
                    try {
                      features = typeof processData.features === 'string'
                        ? JSON.parse(processData.features)
                        : processData.features;
                    } catch (e) {
                      console.error("Error parsing features:", e);
                    }
                  }

                  return Array.isArray(features) ? features
                    .filter(p => p.is_active !== false)
                    .map((principle, index) => {
                      // Dynamic Icon Rendering
                      const IconComponent = {
                        Eye: <Eye className="w-6 h-6" />,
                        Shield: <Shield className="w-6 h-6" />,
                        FileText: <FileText className="w-6 h-6" />,
                        Gavel: <Gavel className="w-6 h-6" />,
                        MapPin: <MapPin className="w-6 h-6" />,
                        Calendar: <Calendar className="w-6 h-6" />,
                        CheckCircle2: <CheckCircle2 className="w-6 h-6" />,
                        TriangleAlert: <TriangleAlert className="w-6 h-6" />,
                        User: <User className="w-6 h-6" />,
                        Building2: <Building2 className="w-6 h-6" />
                      }[principle.icon] || principle.icon;

                      return (
                        <div key={index} className={`principle-item ${principle.color}`}>
                          <div className="principle-icon">{IconComponent}</div>
                          <p className="principle-text">{principle.title}</p>
                        </div>
                      );
                    }) : null;
                })()}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 2: Current Listings */}
      <section className="listings-section" id="available-properties">
        <div className="section-container">
          <div className="listings-header text-center lg:text-left">
            <div>
              <span className="section-tag mx-auto lg:mx-0">Property Listings</span>
              <h2 className="section-title mx-auto lg:mx-0">{listingsSettings?.section_title || 'Available Properties for Auction'}</h2>
              <p className="section-description mx-auto lg:mx-0">
                {listingsSettings?.section_description || 'The list of properties currently available for auction is updated regularly. Each listing includes details such as location, reserve price, inspection schedule, and contact information.'}
              </p>
            </div>
            {/* <div className="listings-filters">
              <button 
                className={`filter-btn ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                All Properties
              </button>
              <button 
                className={`filter-btn ${activeTab === 'active' ? 'active' : ''}`}
                onClick={() => setActiveTab('active')}
              >
                Active Auctions
              </button>
              <button 
                className={`filter-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
                onClick={() => setActiveTab('upcoming')}
              >
                Upcoming
              </button>
            </div> */}
          </div>

          <div className="properties-grid">
            {auctionProperties.map((property, index) => (
              <Card key={property.id} className="property-card relative">
                <div className="absolute top-3 right-3 bg-white text-blue-900 font-bold w-9 h-9 flex items-center justify-center rounded-lg shadow-sm z-10 border border-slate-100 text-sm">
                  #{index + 1}
                </div>
                <div className="property-header">
                  <div className="property-status">
                    <div className={`status-badge ${property.status.toLowerCase()}`}>
                      {property.status}
                    </div>
                    <div className="property-type-badge">{property.type}</div>
                  </div>
                </div>

                <div className="property-content">
                  <h3 className="property-title">{property.title}</h3>
                  <div className="property-location">
                    <MapPin className="w-4 h-4" />
                    <span>{property.location}</span>
                  </div>

                  <div className="property-details">
                    <div className="detail-row">
                      <div className="detail-item">
                        <IndianRupee className="detail-icon" />
                        <div className="detail-info">
                          <span className="detail-label">Reserve Price</span>
                          <span className="detail-value">₹{property.reserve_price}</span>
                        </div>
                      </div>
                      <div className="detail-item">
                        <Building2 className="detail-icon" />
                        <div className="detail-info">
                          <span className="detail-label">EMD Amount</span>
                          <span className="detail-value">₹{property.emd_amount}</span>
                        </div>
                      </div>
                    </div>

                    <div className="detail-row">
                      <div className="detail-item">
                        <Calendar className="detail-icon" />
                        <div className="detail-info">
                          <span className="detail-label">Auction Date</span>
                          <span className="detail-value">{property.auction_date ? new Date(property.auction_date).toLocaleDateString() : 'TBD'}</span>
                        </div>
                      </div>
                      <div className="detail-item">
                        <Calendar className="detail-icon" />
                        <div className="detail-info">
                          <span className="detail-label">Download</span>
                          <div className="flex items-center gap-2">
                            {property.document_url ? (
                              <a
                                href={`${backendBaseURL}${property.document_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
                                title="Download Notice"
                              >
                                <Download className="w-5 h-5" />
                              </a>
                            ) : (
                              <span className="detail-value text-slate-400">Not Available</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button className="property-view-btn"
                    onClick={() => handleEnquireClick(property.title)}>
                    Enquire Us
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          <div className="listings-note">
            <FileText className="note-icon" />
            <p className="note-text">
              <strong>Note:</strong> {listingsSettings?.section_note || 'Property documents and inspection can be arranged upon request. Contact the Authorized Officer for more information.'}
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: How to Participate */}
      <section className="participation-section">
        <div className="section-container">
          <div className="section-header-center text-center">
            <span className="section-tag mx-auto">Getting Started</span>
            <h2 className="section-title mx-auto">{gettingStartedSettings?.section_title || 'Steps to Take Part in the E-Auction'}</h2>
            <p className="section-description mx-auto">
              {gettingStartedSettings?.section_description || 'Participation is open to all eligible bidders who meet the terms and conditions. Follow these simple steps to participate.'}
            </p>
          </div>

          <div className="participation-flow">
            {(() => {
              const activeSteps = steps.filter(step => Boolean(step.is_active));
              // Actually, looking at the admin panel, we toggle is_active boolean.
              // Let's rely on standard filter. 
              const stepsToDisplay = activeSteps.length > 0 ? activeSteps : participationSteps;

              return stepsToDisplay.map((step, index) => {
                const IconComponent = typeof step.icon === 'string' ? (iconMap[step.icon] || FileText) : null;
                // If it's the fallback static data, the icon is already a component element, so handle that
                const iconDisplay = IconComponent ? <IconComponent className="w-5 h-5" /> : step.icon;

                return (
                  <div key={index} className="flow-step">
                    <div className="flow-step-card">
                      <div className="flow-number">{step.step_number || step.number}</div>
                      <div className="flow-icon">{iconDisplay}</div>
                      <h3 className="flow-title">{step.title}</h3>
                      <p className="flow-description">{step.description}</p>
                    </div>
                    {index < stepsToDisplay.length - 1 && (
                      <div className="flow-connector"></div>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {secureBiddingSettings && secureBiddingSettings.is_active && (
            <Card className="bidding-info-card">
              <Shield className="bidding-icon" />
              <div className="bidding-text">
                <h3 className="bidding-title">{secureBiddingSettings.title || 'Secure & Transparent Bidding'}</h3>
                <p className="bidding-description">
                  {secureBiddingSettings.description || 'All bids are recorded and timestamped electronically. The process is monitored to ensure fairness and compliance.'}
                </p>
              </div>
            </Card>
          )}
        </div>
      </section>

      {/* Section 4: Terms & Regulatory Notice */}
      <section className="terms-section">
        <div className="section-container">
          <div className="terms-layout">
            <div className="terms-content text-center lg:text-left">
              <span className="section-tag mx-auto lg:mx-0">Legal Information</span>
              <h2 className="section-title mx-auto lg:mx-0">{legalSettings?.section_title || 'Compliance & Legal Information'}</h2>
              <p className="terms-intro mx-auto lg:mx-0">
                {legalSettings?.section_description || 'The e-auction is conducted in accordance with applicable laws and regulatory guidelines. Participants are advised to read the detailed terms and conditions before placing bids.'}
              </p>

              <div className="important-points">
                <h3 className="important-heading">Important</h3>
                <ul className="important-list">
                  {(() => {
                    const activePoints = importantPoints.filter(point => point.is_active);
                    const pointsToDisplay = activePoints.length > 0 ? activePoints : fallbackImportantPoints.map((text, idx) => ({ point_text: text, id: idx }));

                    return pointsToDisplay.map((point, index) => (
                      <li key={point.id || index} className="important-item">
                        <TriangleAlert className="important-icon" />
                        <span>{point.point_text || point}</span>
                      </li>
                    ));
                  })()}
                </ul>
              </div>
            </div>

            <Card className="regulatory-card">
              <div className="regulatory-header">
                <Shield className="regulatory-icon" />
                <h3 className="regulatory-title">{legalSettings?.regulatory_title || 'Regulatory Advisory'}</h3>
              </div>
              <p className="regulatory-text">
                {legalSettings?.regulatory_description || 'Disputes, if any, will be handled as per applicable legal provisions.'}
              </p>
              <div className="regulatory-actions">
                {/* <Button className="regulatory-btn">
                  <Download className="w-4 h-4" />
                  Download Terms & Conditions
                </Button> */}
                {/* <Button variant="outline" className="regulatory-btn">
                  Contact Team
                </Button> */}
                {/* <Button
                  variant="outline"
                  className="regulatory-btn"
                  onClick={() => navigate(legalSettings?.button_link || "/contact")}
                >
                  {legalSettings?.button_text || 'Contact Team'}
                </Button> */}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="auction-cta">
        <div className="auction-cta-container">
          <Gavel className="cta-gavel-icon" />
          <h2 className="cta-auction-title">Ready to Participate?</h2>
          <p className="cta-auction-text">
            Register now to receive notifications about upcoming auctions and property listings.
          </p>
          <div className="cta-auction-actions">
            <Button className="cta-auction-primary">
              Register for E-Auction
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button variant="outline" className="cta-auction-secondary">
              Contact Authorized Officer
            </Button>
          </div>
        </div>
      </section> */}

      {/* Auction Enquiry Modal */}
      <AuctionEnquiryModal
        isOpen={isEnquiryModalOpen}
        onClose={() => setIsEnquiryModalOpen(false)}
        propertyTitle={selectedProperty}
      />

    </div>
  );
};

export default EAuction;